import React, { useState } from 'react';
import { ChevronDown, ChevronUp, Sun, Heart, Users, UserCheck, GraduationCap, Infinity, Smile, HandHeart } from 'lucide-react';
import { HappyRule } from '../types';

const RULES: HappyRule[] = [
  { id: 1, title: "The Task of Love", concept: "From Me to Us", description: "Happiness is not something you find alone. It is found in community. To be happy, you must switch the subject of your life from 'I' to 'We'.", icon: "Heart" },
  { id: 2, title: "Self-Reliance", concept: "Independence", description: "Education is not about teaching facts, but teaching self-reliance. True independence is knowing you have worth without needing the praise of others.", icon: "UserCheck" },
  { id: 3, title: "Respect", concept: "Seeing Others", description: "Respect is seeing a person as they truly are, and being aware of their uniqueness. It is the basis of all love.", icon: "Users" },
  { id: 4, title: "The Best Parting", concept: "Relationships", description: "In all relationships, always be ready for the 'Best Parting'. Live in such a way that if you parted today, you would have no regrets.", icon: "HandHeart" },
  { id: 5, title: "Love is a Decision", concept: "Commitment", description: "Love is not something you fall into. It is a task you build. It requires the courage to trust unconditionally.", icon: "Sun" },
  { id: 6, title: "Life is Simple", concept: "Simplicity", description: "The world is simple, and life is simple. It is you who makes it complex with your subjective interpretations.", icon: "Smile" },
  { id: 7, title: "The Golden Rule", concept: "Empathy", description: "Treat others not as they treat you, but as you wish to be treated. Be the one to start the positive cycle.", icon: "Infinity" },
  { id: 8, title: "Courage to be Normal", concept: "Acceptance", description: "You do not need to be special to have worth. Accept your ordinary self, and you will find extraordinary peace.", icon: "GraduationCap" }
];

const IconMap: Record<string, React.ReactNode> = {
  Heart: <Heart size={24} />,
  UserCheck: <UserCheck size={24} />,
  Users: <Users size={24} />,
  HandHeart: <HandHeart size={24} />,
  Sun: <Sun size={24} />,
  Smile: <Smile size={24} />,
  Infinity: <Infinity size={24} />,
  GraduationCap: <GraduationCap size={24} />,
};

const Happy: React.FC = () => {
  const [openId, setOpenId] = useState<number | null>(1);

  const toggle = (id: number) => {
    setOpenId(openId === id ? null : id);
  };

  return (
    <div className="max-w-3xl mx-auto px-6 pb-20 animate-fadeIn">
      <header className="mb-10 text-center">
        <div className="inline-block p-3 bg-yellow-50 text-yellow-600 rounded-full mb-4">
          <Sun size={32} />
        </div>
        <h1 className="text-4xl font-serif font-bold text-nunchi-900 mb-3">The Courage to Be Happy</h1>
        <p className="text-xl text-nunchi-500 italic font-serif mb-6">"Happiness is the courage to be normal."</p>
        <p className="text-nunchi-600 max-w-xl mx-auto leading-relaxed">
          The sequel to Adlerian philosophy focuses on the practical application of courage: 
          how to love, how to be self-reliant, and how to find community.
        </p>
      </header>

      <div className="space-y-4">
        {RULES.map((rule) => (
          <div 
            key={rule.id} 
            className={`bg-white border rounded-xl overflow-hidden transition-all duration-300 ${openId === rule.id ? 'border-yellow-400 shadow-md' : 'border-nunchi-200 hover:border-yellow-300'}`}
          >
            <button 
              onClick={() => toggle(rule.id)}
              className="w-full flex items-center justify-between p-5 text-left focus:outline-none"
            >
              <div className="flex items-center gap-4">
                <div className={`p-2 rounded-lg ${openId === rule.id ? 'bg-yellow-100 text-yellow-600' : 'bg-gray-50 text-gray-400'}`}>
                  {IconMap[rule.icon]}
                </div>
                <div>
                  <h3 className={`font-bold text-lg ${openId === rule.id ? 'text-nunchi-900' : 'text-gray-700'}`}>
                    {rule.title}
                  </h3>
                  <p className="text-xs text-nunchi-500 font-serif uppercase tracking-wider">{rule.concept}</p>
                </div>
              </div>
              <div className="text-nunchi-400">
                {openId === rule.id ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </div>
            </button>
            
            <div className={`overflow-hidden transition-[max-height] duration-500 ease-in-out ${openId === rule.id ? 'max-h-40' : 'max-h-0'}`}>
              <div className="p-5 pt-0 text-nunchi-600 leading-relaxed border-t border-dashed border-yellow-200 mt-2">
                {rule.description}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Happy;